// Today's "Awake hour": what to do with him between naps, step by step, ticking
// off from the log (domain/awakeplan.ts). Shown while he's up in the day.

import { Check, GraduationCap, Moon, Sun, ToyBrick, Utensils, Droplet } from 'lucide-react';
import { useCollection, useEvents, useHousehold, useLiveStatus } from '../../data/hooks.ts';
import { awakeWindowMinutes } from '../../domain/age.ts';
import { awakePlan, type AwakePlan, type AwakeStep } from '../../domain/awakeplan.ts';
import { isDateMilestone } from '../../domain/care.ts';
import { upcomingMeal } from '../../domain/feeding.ts';
import { bedtimePending } from '../../domain/sleep.ts';
import { formatDuration, formatTime, inClockWindow, today } from '../../domain/time.ts';
import { denWord } from '../../domain/types.ts';
import { Button, Card, CardHeader, Sheet } from '../../ui/primitives.tsx';
import { openNew } from '../log/EventEditor.tsx';
import { quickMeal, quickWee, toggleSleep, toggleTimed } from '../log/quick.ts';

export type AwakeCtx = { plan: AwakePlan; now: number; den: string; meal: ReturnType<typeof upcomingMeal> };

/** The plan for right now, or undefined when he's asleep, it's night or bedtime, or the log has a gap. */
export function useAwakePlan(): AwakeCtx | undefined {
  const s = useLiveStatus(15_000);
  const household = useHousehold();
  const events = useEvents();
  const care = useCollection('care');
  const { sleep, now, weeks } = s;
  const night = { start: household.nightStart, end: household.nightEnd };
  if (sleep.asleep || sleep.stale || sleep.wokeAt === undefined) return undefined;
  if (inClockWindow(now, night.start, night.end) || bedtimePending(events, now, night)) return undefined;
  const den = denWord(household.den);
  const meal = upcomingMeal(s.meals, now);
  const goOut = care.find(isDateMilestone)?.due;
  const plan = awakePlan({
    now,
    wokeAt: sleep.wokeAt,
    windowMin: awakeWindowMinutes(weeks),
    events,
    mealDueAt: meal.meal?.at,
    den,
    canGoOut: goOut !== undefined && goOut <= today(now),
    toiletDue: !s.toilet.stale && (s.toilet.urgency === 'now' || s.toilet.urgency === 'overdue') ? { reason: s.toilet.reason } : undefined,
  });
  return { plan, now, den, meal };
}

const subLine = ({ plan, now }: AwakeCtx) => `Up ${formatDuration(now - plan.wokeAt)} · nap ${now > plan.napAt ? `was due ${formatTime(plan.napAt)}` : `about ${formatTime(plan.napAt)}`}`;

/** The kitchen tablet: the full plan as a card in the side column. */
export function AwakePlanCard() {
  const ctx = useAwakePlan();
  if (!ctx) return null;
  return (
    <Card className="awake-plan">
      <CardHeader title="Awake hour" icon={Sun} sub={subLine(ctx)} />
      <PlanBody ctx={ctx} />
    </Card>
  );
}

/** The full plan in a sheet, opened from the Now card on phones and desktops. */
export function AwakePlanSheet({ ctx, open, onClose }: { ctx: AwakeCtx; open: boolean; onClose: () => void }) {
  return (
    <Sheet open={open} onClose={onClose} title="Awake hour">
      <p className="muted small awake-sheet-sub">{subLine(ctx)}</p>
      <PlanBody ctx={ctx} />
    </Sheet>
  );
}

function PlanBody({ ctx }: { ctx: AwakeCtx }) {
  const { plan, now, den, meal } = ctx;
  const over = now > plan.napAt;
  return (
    <>
      <div className={`awake-bar${over ? ' over' : ''}`} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(plan.progress * 100)} aria-label="Through his awake time">
        <span style={{ width: `${Math.round(plan.progress * 100)}%` }} />
      </div>
      <ol className="awake-steps">
        {plan.steps.map((st, i) => (
          <li key={st.id} className={`awake-step ${st.state}`} aria-current={st.state === 'now' ? 'step' : undefined}>
            <span className="awake-dot" aria-hidden>
              {st.state === 'done' ? <Check /> : i + 1}
            </span>
            <div className="awake-text">
              <strong>{st.title}</strong>
              {st.state === 'done' && st.doneAt !== undefined && <small className="muted"> · {formatTime(st.doneAt)}</small>}
              {st.state === 'skipped' && <small className="muted"> · skipped</small>}
              {st.state === 'next' && st.id === 'meal' && meal.meal && <small className="muted"> · due {meal.meal.time}</small>}
              {st.state === 'now' && <StepNow st={st} den={den} grams={meal.meal?.grams} />}
            </div>
          </li>
        ))}
      </ol>
    </>
  );
}

function StepNow({ st, den, grams }: { st: AwakeStep; den: string; grams?: number }) {
  return (
    <div className="awake-now">
      <p className="small">{st.detail}</p>
      {st.ideas && (
        <ul className="awake-ideas small">
          {st.ideas.map((x) => (
            <li key={x}>{x}</li>
          ))}
        </ul>
      )}
      <div className="row awake-actions">
        {(st.id === 'wee' || st.id === 'out') && (
          <Button size="sm" variant="primary" icon={Droplet} onClick={quickWee}>
            Log wee outside
          </Button>
        )}
        {st.id === 'meal' && (
          <Button size="sm" variant="primary" icon={Utensils} onClick={() => quickMeal(grams)}>
            Log meal
          </Button>
        )}
        {st.id === 'active' && (
          <>
            <Button size="sm" variant="primary" icon={GraduationCap} onClick={() => openNew('training', { skills: [] })}>
              Log training
            </Button>
            <Button size="sm" icon={ToyBrick} onClick={() => toggleTimed('play')}>
              Start play
            </Button>
          </>
        )}
        {st.id === 'nap' && (
          <>
            {st.beenOut === undefined && (
              <Button size="sm" icon={Droplet} onClick={quickWee}>
                Log wee
              </Button>
            )}
            <Button size="sm" variant="primary" icon={Moon} onClick={toggleSleep}>
              Nap in his {den}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
